import {
  IonButton,
  IonCheckbox,
  IonInput,
  IonLabel,
  IonModal,
} from "@ionic/react";
import { useEffect, useState } from "react";
import TimePicker from "../Custom/TimePicker";
import {
  ConsentFormDetails,
  ConsentFormErrors,
} from "../../models/ConsentForm";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Loader from "../LoaderComponent";
import { useRecoilState } from "recoil";
import {
  allExercisesState,
  isShowToast,
  networkConnectionStatus,
  PatientState,
} from "../../data/atoms";
import { ActionType } from "../../models/Enums";
import { ValidateEmailAndPhoneNum } from "../../services/AuthServices";
import { useHistory } from "react-router";
import {
  getPatientIntegrationLock,
  parseDateTime,
  toLocalISOString,
} from "../../utils/sharedFunction";
import SetupEpisodeModal from "./setup-episode-modal";
import { PatientVM } from "../../models/PatientVM";
import ErrorTopToaster from "../ErrorTopToaster";

interface Props {
  setConsentForm: Function;
  patientName: string;
  phoneNumber: string;
  email: string;
  onClose?: () => void;
  consentFormObjectPassToParent?: (obj: any) => void;
}

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

const ConsentFormModal: React.FC<Props> = ({
  setConsentForm,
  patientName,
  phoneNumber,
  email,
  onClose,
  consentFormObjectPassToParent,
}) => {
  const [selectedTime, setSelectedTime] = useState<string>("NA");
  const [consentFormDetails, setConsentFormDetails] =
    useState<ConsentFormDetails>({
      patientName: "",
      email: "",
      phoneNumber: "",
      time: "NA",
      date: "",
      isChecked: false,
    });
  const [errors, setErrors] = useState<ConsentFormErrors>({});
  const [value, onChange] = useState<Value>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ShowNetWorkRecoil, setShowNetWorkRecoil] = useRecoilState(
    networkConnectionStatus,
  );
  const [showToast, setShowToast] = useRecoilState(isShowToast);
  const history = useHistory();
  const [isPhoneAvailable, setIsPhoneAvailable] = useState<boolean>(false);
  const [isEmailAvailable, setIsEmailAvailable] = useState<boolean>(false);
  const [SetupEpisode, setSetupEpisode] = useState(false);
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [allExercises, setAllExercisesState] =
    useRecoilState(allExercisesState);
  const [patientDetailsRecoil, setPatientDetailsRecoil] =
    useRecoilState(PatientState);
  const [patientDetails, setPatientDetails] =
    useState<PatientVM>(patientDetailsRecoil);
  const patientIntegrationLock = getPatientIntegrationLock(
    patientDetailsRecoil
  );
  const isCurrentMonth =
    activeStartDate.getFullYear() === new Date().getFullYear() &&
    activeStartDate.getMonth() === new Date().getMonth();
  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const calendar = document.getElementById("calendar-container");
      if (calendar && !calendar.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (patientName != null) {
      setConsentFormDetails((prevState) => ({
        ...prevState,
        patientName: patientName,
        email: email,
        phoneNumber: phoneNumber,
      }));
    }
    if (phoneNumber && phoneNumber.trim() !== "") {
      setIsPhoneAvailable(true);
    }
    if (email && email.trim() !== "") {
      setIsEmailAvailable(true);
    }
  }, [patientName, phoneNumber, email]);

  const validateForm = (): boolean => {
    const newErrors: ConsentFormErrors = {};

    // Patient Name validation
    if (!consentFormDetails.patientName?.trim()) {
      newErrors.patientName = "Patient name is required";
    }

    // Phone Number validation (optional)
    if (consentFormDetails.phoneNumber?.trim()) {
      if (
        !/^([0-9]( |-)?)?(\(?[0-9]{3}\)?|[0-9]{3})( |-)?([0-9]{3}( |-)?[0-9]{4,5})$/.test(
          consentFormDetails.phoneNumber,
        )
      ) {
        newErrors.phoneNumber = "Invalid phone number format";
      }
    }

    // Email validation
    if (
      consentFormDetails.email?.trim() &&
      !/^\w+(?:[.-]\w+)*@\w+(?:[.-]\w+)*\.\w{2,3}$/.test(
        consentFormDetails.email,
      )
    ) {
      newErrors.email = "Invalid email format";
    }

    if (
      !consentFormDetails.email?.trim() &&
      !consentFormDetails.phoneNumber?.trim()
    ) {
      newErrors.email = "both_empty";
      newErrors.phoneNumber = "both_empty";
      setShowToast({
        show: true,
        string: "Please enter either email or phone number",
      });
    }

    // Date validation
    if (!consentFormDetails.date) {
      newErrors.date = "Start date is required";
    }

    // Time validation - Optional, no error if not set (defaults to "NA")
    // if (!consentFormDetails.time || consentFormDetails.time === "NA") {
    //   newErrors.time = "Start time is required";
    // }

    // Consent validation
    if (!consentFormDetails.isChecked) {
      newErrors.consent = "You must consent to receive SMS reminders";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("consentFormDetails: ", consentFormDetails);
    if (!isButtonDisabled) {
      setIsButtonDisabled(true);
      if (!validateForm()) {
        setIsButtonDisabled(false);
        return;
      }
      if (!navigator.onLine) {
        setShowNetWorkRecoil({ isShown: true });
        return;
      }
      try {
        const startDateLocal = new Date(consentFormDetails.date);
        const datePart = consentFormDetails.date.split("T")[0]; // "2025-09-02"

        let exerciseStartLocalT = "";
        let exerciseStartUtc = "";

        if (consentFormDetails.time !== "NA") {
          const exerciseStartLocal = parseDateTime(
            datePart,
            consentFormDetails.time,
          );
          exerciseStartLocalT = toLocalISOString(exerciseStartLocal);
          exerciseStartUtc = exerciseStartLocal.toISOString();
        } else {
          exerciseStartLocalT = "NA";
          exerciseStartUtc = "NA";
        }

        const startDateLocalT = toLocalISOString(startDateLocal);
        // const exerciseStartLocal = parseDateTime(
        //   datePart,
        //   consentFormDetails.time
        // );
        // const exerciseStartLocalT = toLocalISOString(exerciseStartLocal);
        // const exerciseStartUtc = exerciseStartLocal.toISOString(); // ✅ Works now

        // Convert RTMStartDate to avoid timezone issues
        console.log("Original RTM Start Date: ", consentFormDetails.date);
        let rtmStartDate = new Date(consentFormDetails.date);
        let rtmStartDateLocal: string | Date = rtmStartDate;
        let rtmStartDateUtc: string | Date = rtmStartDate;

        // Normalize date: local version uses local time components, UTC version uses UTC time components
        if (rtmStartDate instanceof Date) {
          const localYear = rtmStartDate.getFullYear();
          const localMonth = (rtmStartDate.getMonth() + 1)
            .toString()
            .padStart(2, "0");
          const localDay = rtmStartDate.getDate().toString().padStart(2, "0");
          const localHour = rtmStartDate.getHours().toString().padStart(2, "0");
          const localMinute = rtmStartDate
            .getMinutes()
            .toString()
            .padStart(2, "0");
          const localSecond = rtmStartDate
            .getSeconds()
            .toString()
            .padStart(2, "0");
          rtmStartDateLocal = `${localYear}-${localMonth}-${localDay}T${localHour}:${localMinute}:${localSecond}`;

          const utcYear = rtmStartDate.getUTCFullYear();
          const utcMonth = (rtmStartDate.getUTCMonth() + 1)
            .toString()
            .padStart(2, "0");
          const utcDay = rtmStartDate.getUTCDate().toString().padStart(2, "0");
          const utcHour = rtmStartDate
            .getUTCHours()
            .toString()
            .padStart(2, "0");
          const utcMinute = rtmStartDate
            .getUTCMinutes()
            .toString()
            .padStart(2, "0");
          const utcSecond = rtmStartDate
            .getUTCSeconds()
            .toString()
            .padStart(2, "0");
          rtmStartDateUtc = `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}:${utcSecond}`;
        }

        console.log(
          "RTM Start Date Local (no timezone shift): ",
          rtmStartDateLocal,
        );
        console.log("RTM Start Date UTC: ", rtmStartDateUtc);
        console.log(
          "Exercise Start Time Local (no timezone shift): ",
          exerciseStartLocalT,
        );
        console.log("Exercise Start Time UTC: ", exerciseStartUtc);

        const ConsentForm: any = {
          PatientId: localStorage.getItem("patientServerID"),
          EncryptedServerId: localStorage.getItem("EncryptedServerId"),
          MobilePhone: consentFormDetails.phoneNumber.replace(/-/g, ""),
          Email: consentFormDetails.email,
          StartDate: rtmStartDateUtc,
          ExerciseStartTime: exerciseStartLocalT,
          ExerciseStartTimeUtc: exerciseStartUtc,
          LocalDateTime: rtmStartDateLocal as string, // RTM start date in local time
          UTCDateTime: rtmStartDateUtc as string, // RTM start date in UTC
        };
        await ValidateEmailAndPhoneNum({
          Email: consentFormDetails.email,
          PhoneNo: consentFormDetails.phoneNumber.replace(/-/g, ""),
          PatientId: localStorage.getItem("patientServerID"),
          EncryptedServerId: localStorage.getItem("EncryptedServerId"),
        }).then(async function (response: any) {
          console.log("Response: ", response);
          debugger;
          setIsButtonDisabled(false);
          if (response?.data?.isResponseSuccessfull === true) {
            //setSetupEpisode(true);
            consentFormObjectPassToParent &&
              consentFormObjectPassToParent(ConsentForm);

            setAllExercisesState((prevState: any) => ({
              ...prevState,
              patientDetails: {
                ...prevState.patientDetails,
                email: consentFormDetails.email,
                phoneNumber: consentFormDetails.phoneNumber.replace(/-/g, ""),
              },
            }));

            setPatientDetails((prevState: PatientVM) => ({
              ...prevState,
              email: consentFormDetails.email || "",
              phoneNumber:
                consentFormDetails.phoneNumber.replace(/-/g, "") || "",
              mobilePhone:
                consentFormDetails.phoneNumber.replace(/-/g, "") || "",
            }));
            setPatientDetailsRecoil((prevState: PatientVM) => ({
              ...prevState,
              email: consentFormDetails.email || "",
              phoneNumber:
                consentFormDetails.phoneNumber.replace(/-/g, "") || "",
              mobilePhone:
                consentFormDetails.phoneNumber.replace(/-/g, "") || "",
            }));
          }
          if (response?.data?.isResponseSuccessfull === false) {
            if (response?.data?.id === 3) {
              setErrors((prev) => ({
                ...prev,
                email: "This email address already exists",
              }));
            } else if (response?.data?.id === 4) {
              setErrors((prev) => ({
                ...prev,
                phoneNumber: "This phone number already exists",
              }));
            } else if (response?.data?.id === 2) {
              setErrors((prev) => ({
                ...prev,
                email: "This email address already exists",
                phoneNumber: "This phone number already exists",
              }));
            }
          }
          console.log("patientDetails after consent ", patientDetails);
          console.log(
            "patientDetailsRecoil after consent ",
            patientDetailsRecoil,
          );
        });
      } catch (error) {
        history.push("/confirmation", {
          actionType: ActionType.FailureAPIResponse,
        });
        console.error(error);
      }
    }
  };

  return !isLoading ? (
    <>
      {showToast.show && <ErrorTopToaster errorString={showToast.string} />}
      <div className="consent-form-modal ">
        <h1 className="fz-32 fw-700 align-center text-charcoal-blue mb-16">
          Consent Form to receive SMS Reminders
        </h1>
        <h1 className="fz-20 fw-400 align-center text-charcoal-blue mb-24">
          We’ll use the phone number and email to contact you about the daily
          exercise plan. This information will also be saved in your profile for
          future use.
        </h1>

        <div
          className={`field-wrap ${errors?.patientName ? "error-wrap" : ""}`}
        >
          <IonLabel className="label" placeholder={undefined}>
            Patient Name *
          </IonLabel>
          <IonInput
            className="input-field"
            type="text"
            placeholder="Enter Patient Name"
            value={consentFormDetails?.patientName}
            readonly
          ></IonInput>
          {errors?.patientName && (
            <p className="error-msg">{errors?.patientName}</p>
          )}
        </div>

        <div
          className={`field-wrap ${errors?.phoneNumber ? "error-wrap" : ""}`}
        >
          <IonLabel className="label" placeholder={undefined}>
            Phone Number
          </IonLabel>
          <IonInput
            className="input-field"
            type="text"
            placeholder="Enter Phone number"
            value={consentFormDetails?.phoneNumber}
            disabled={patientIntegrationLock.disableIdentityFields}
            onIonChange={(e: any) => {
              if (e.target.value.length <= 13) {
                if (
                  e.target.value.length === 3 &&
                  consentFormDetails?.phoneNumber?.indexOf("-") === -1
                ) {
                  e.target.value = e.target.value + "-";
                } else if (
                  e.target.value.length === 7 &&
                  consentFormDetails?.phoneNumber?.lastIndexOf("-") === 3
                ) {
                  e.target.value = e.target.value + "-";
                }
                setConsentFormDetails((prevState) => ({
                  ...prevState,
                  phoneNumber: e.target.value,
                }));
              } else {
                e.target.value = consentFormDetails?.phoneNumber || "";
              }
            }}
          ></IonInput>
          {errors?.phoneNumber && errors?.phoneNumber !== "both_empty" && (
            <p className="error-msg">{errors?.phoneNumber}</p>
          )}
        </div>

        <div className={`field-wrap ${errors?.email && "error-wrap"}`}>
          <IonLabel className="label" placeholder={undefined}>
            Email
          </IonLabel>
          <IonInput
            className="input-field"
            type="email"
            placeholder="Enter Email"
            value={consentFormDetails?.email}
            disabled={patientIntegrationLock.disableIntegrationOnlyFields}
            // readonly={isEmailAvailable}
            onIonChange={(e: any) => {
              setConsentFormDetails((prevState: any) => ({
                ...prevState,
                email: e.target.value,
              }));
            }}
          ></IonInput>
          {errors?.email && errors?.email !== "both_empty" && (
            <p className="error-msg">{errors?.email}</p>
          )}
        </div>

        <div className={`field-wrap ${errors?.date && "error-wrap"}`}>
          <IonLabel className="label" placeholder={undefined}>
            RTM Start Date *
          </IonLabel>
          <IonInput
            className="input-field"
            type="text"
            placeholder="MM/DD/YYYY"
            value={value instanceof Date ? value.toLocaleDateString() : ""}
            readonly
            onClick={() => setShowCalendar(!showCalendar)}
          />
          {errors?.date && <p className="error-msg">{errors?.date}</p>}

          {showCalendar && (
            <div id="calendar-container" className="datepicker-calendar">
              <Calendar
                formatShortWeekday={(locale, date) =>
                  date
                    .toLocaleDateString(locale, { weekday: "short" })
                    .slice(0, 2)
                }
                prevLabel={
                  isCurrentMonth ? null : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="22"
                      viewBox="0 0 13 22"
                      fill="none"
                    >
                      <path
                        d="M10.5664 2.02707L1.51226 11.0812L10.5664 20.1354"
                        stroke="#707070"
                        stroke-width="3.01805"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  )
                }
                nextLabel={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="29"
                    height="28"
                    viewBox="0 0 29 28"
                    fill="none"
                  >
                    <path
                      d="M9.92578 5.02719L18.9799 14.0813L9.92578 23.1355"
                      stroke="#707070"
                      stroke-width="3.01805"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                }
                prev2Label={null} // optional: hide double arrows
                next2Label={null}
                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                tileDisabled={({ date }) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0))
                }
                onActiveStartDateChange={({ activeStartDate }) =>
                  setActiveStartDate(activeStartDate || new Date())
                }
                onChange={(date) => {
                  onChange(date);
                  setShowCalendar(false);
                  // Normalize date to avoid timezone issues, use current local time
                  let normalizedDate = "";
                  if (date instanceof Date) {
                    const now = new Date();
                    const year = date.getFullYear();
                    const month = (date.getMonth() + 1)
                      .toString()
                      .padStart(2, "0");
                    const day = date.getDate().toString().padStart(2, "0");
                    const hour = now.getHours().toString().padStart(2, "0");
                    const minute = now.getMinutes().toString().padStart(2, "0");
                    const second = now.getSeconds().toString().padStart(2, "0");
                    normalizedDate = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
                  }
                  setConsentFormDetails((prev) => ({
                    ...prev,
                    date: normalizedDate,
                  }));
                }}
                value={value}
              />
            </div>
          )}
        </div>

        <div className={`field-wrap ${errors?.time && "error-wrap"}`}>
          <IonLabel className="label" placeholder={undefined}>
            Preferred Daily Exercise Start Time
          </IonLabel>
          <TimePicker
            value={selectedTime}
            onChange={(time: string) => {
              setSelectedTime(time);
              setConsentFormDetails((prev) => ({
                ...prev,
                time: time,
              }));
            }}
            placeholder="NA"
          />
          {errors?.time && <p className="error-msg">{errors?.time}</p>}
        </div>

        <div className={`field-wrap ${errors.consent ? "error-wrap" : ""}`}>
          <label className="custom-checkbox text-charcoal-blue">
            <IonCheckbox
              placeholder={undefined}
              checked={consentFormDetails.isChecked}
              onIonChange={(e: CustomEvent) => {
                setConsentFormDetails((prevState) => ({
                  ...prevState,
                  isChecked: e.detail.checked,
                }));
              }}
            />
            <span className="checkmark"></span>By providing my mobile number and
            checking this box, I agree that VirtuSense may send me recurring SMS
            messages about my care plan (e.g., daily exercise reminders and
            educational instructions). If I do not complete my exercises on a
            given day, I will reply 'NO' to the reminder message. Message
            frequency may vary. Msg & data rates may apply. Reply STOP to
            cancel.
          </label>
          {errors.consent && <p className="error-msg">{errors.consent}</p>}
        </div>
        <div className="d-flex justify-content-center gap-24 mb-24">
          <a
            href="#"
            className="generic-text-btn d-flex "
            onClick={(e) => {
              e.preventDefault(); // prevent default browser navigation
              if ((window as any).webkit?.messageHandlers?.sendToIoS) {
                (window as any).webkit.messageHandlers.sendToIoS.postMessage({
                  methodName: "loadWebContent",
                  webContentUrl:
                    "https://www.virtusense.ai/legal/terms-of-service",
                });
              } else {
                // fallback for web browsers
                window.open(
                  "https://www.virtusense.ai/legal/terms-of-service",
                  "_blank",
                  "noopener,noreferrer",
                );
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
            >
              <path
                d="M14.1125 1.475C14.3469 0.9125 14.8953 0.546875 15.5 0.546875H23C23.8297 0.546875 24.5 1.21719 24.5 2.04688V9.54688C24.5 10.1516 24.1344 10.7 23.5719 10.9344C23.0094 11.1688 22.3672 11.0375 21.9359 10.6109L19.25 7.92031L12.0594 15.1063C11.4734 15.6922 10.5219 15.6922 9.93594 15.1063C9.35 14.5203 9.35 13.5687 9.93594 12.9828L17.1266 5.79688L14.4406 3.10625C14.0094 2.675 13.8828 2.03281 14.1172 1.47031L14.1125 1.475ZM0.5 8.79688C0.5 6.725 2.17812 5.04688 4.25 5.04688H8C8.82969 5.04688 9.5 5.71719 9.5 6.54688C9.5 7.37656 8.82969 8.04688 8 8.04688H4.25C3.8375 8.04688 3.5 8.38438 3.5 8.79688V20.7969C3.5 21.2094 3.8375 21.5469 4.25 21.5469H16.25C16.6625 21.5469 17 21.2094 17 20.7969V17.0469C17 16.2172 17.6703 15.5469 18.5 15.5469C19.3297 15.5469 20 16.2172 20 17.0469V20.7969C20 22.8687 18.3219 24.5469 16.25 24.5469H4.25C2.17812 24.5469 0.5 22.8687 0.5 20.7969V8.79688Z"
                fill="#802392"
              />
            </svg>{" "}
            Terms and Conditions
          </a>
          <a
            href="#"
            className="generic-text-btn d-flex"
            onClick={(e) => {
              e.preventDefault(); // prevent default browser navigation
              if ((window as any).webkit?.messageHandlers?.sendToIoS) {
                (window as any).webkit.messageHandlers.sendToIoS.postMessage({
                  methodName: "loadWebContent",
                  webContentUrl:
                    "https://www.virtusense.ai/legal/privacy-policy",
                });
              } else {
                // fallback for web browsers
                window.open(
                  "https://www.virtusense.ai/legal/privacy-policy",
                  "_blank",
                  "noopener,noreferrer",
                );
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
            >
              <path
                d="M14.1125 1.475C14.3469 0.9125 14.8953 0.546875 15.5 0.546875H23C23.8297 0.546875 24.5 1.21719 24.5 2.04688V9.54688C24.5 10.1516 24.1344 10.7 23.5719 10.9344C23.0094 11.1688 22.3672 11.0375 21.9359 10.6109L19.25 7.92031L12.0594 15.1063C11.4734 15.6922 10.5219 15.6922 9.93594 15.1063C9.35 14.5203 9.35 13.5687 9.93594 12.9828L17.1266 5.79688L14.4406 3.10625C14.0094 2.675 13.8828 2.03281 14.1172 1.47031L14.1125 1.475ZM0.5 8.79688C0.5 6.725 2.17812 5.04688 4.25 5.04688H8C8.82969 5.04688 9.5 5.71719 9.5 6.54688C9.5 7.37656 8.82969 8.04688 8 8.04688H4.25C3.8375 8.04688 3.5 8.38438 3.5 8.79688V20.7969C3.5 21.2094 3.8375 21.5469 4.25 21.5469H16.25C16.6625 21.5469 17 21.2094 17 20.7969V17.0469C17 16.2172 17.6703 15.5469 18.5 15.5469C19.3297 15.5469 20 16.2172 20 17.0469V20.7969C20 22.8687 18.3219 24.5469 16.25 24.5469H4.25C2.17812 24.5469 0.5 22.8687 0.5 20.7969V8.79688Z"
                fill="#802392"
              />
            </svg>{" "}
            Privacy Policy
          </a>
        </div>
        <div className="two-col-btn">
          <IonButton
            placeholder={undefined}
            className="blue-outline-btn h-64"
            onClick={() => {
              setConsentForm(false);
            }}
          >
            {" "}
            Cancel
          </IonButton>
          <IonButton
            placeholder={undefined}
            className="blue-btn h-64"
            onClick={handleSubmit}
            disabled={isButtonDisabled}
          >
            {" "}
            Confirm
          </IonButton>
        </div>
      </div>

      <IonModal
        className="generic-modal-96-vw"
        backdropDismiss={false}
        isOpen={SetupEpisode}
        placeholder={undefined}
      >
        <SetupEpisodeModal
          setSetupEpisode={setSetupEpisode}
          rtmStartDate={consentFormDetails.date || undefined}
        />
      </IonModal>
    </>
  ) : (
    <Loader />
  );
};
export default ConsentFormModal;
